// angular
import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';
import { SocketFactory } from './shared/services/socket-factory.service';
// interceptors
import { BaseInterceptor } from '@core/interceptors/base.interceptor';
import { AuthorizationInterceptor } from '@core/interceptors/authorization.interceptor';
// toast service
import { provideToastr } from 'ngx-toastr';
// services
import { BrowserStorageService } from '@shared/services/browser-storage.service';
import { UserService } from '@shared/services/user.service';
import { PwjToastService } from '@core/shared/services/app-toast.service';
import { StorageService } from '@shared/services/storage.service';
import { AuctionsService } from '@auctions/shared/services/auctions.service';
import { CategoriesService } from '@categories/shared/services/categories.service';
import { ChatService } from './+chat/shared/services/chat.service';
import { MessagingService } from './+chat/shared/services/messaging.service';
import { StateManagementService } from './+chat/shared/services/state-management.service';
import { ProductsService } from '@products/shared/services/products.service';
import { QuickOrderService } from '@quick-order/shared/services/quick-order.service';
import { ElasticSearchService } from '@search/shared/services/elastic-search.service';
import { SuppliersService } from '@suppliers/shared/services/suppliers.service';
import { TendersService } from '@tenders/shared/services/tenders.service';
import { ProjectsService } from '@projects/shared/services/projects.service';
import { CommentsService } from '@core/shared/services/comments.service';
import { SolutionsService } from '@core/shared/services/solutions.service';
import { DialogService } from '@core/shared/services/dialog.service';
import { LayoutService } from '@layout/shared/services/layout.service';
import { FavoritesService } from '@profile/profile-favorites/shared/favorites.service';
import { CanonicalService } from '@shared/services/canonical.service';
import { UploaderService } from '@core/shared/services/uploader.service';
import { InitService } from '@shared/services/init.service';
import { CurrencyService } from '@core/shared/services/currency.service';
import { HelpService } from '@help/shared/services/help.service';
import { HandleErrorService } from '@shared/services/handle-error.service';
import { RecaptchaService } from '@shared/services/recaptcha.service';
import { AuthService } from '@shared/services/auth.service';
import { SeoTagsService } from '@shared/services/seo-tags.service';
// firebase
import { FirebaseService } from '@shared/services/firebase.service';
// notification service
import { PushNotificationsService } from '@shared/services/push-notifications.service';
// pipes
import { HasAccessPipe } from '@shared/pipes/has-access.pipe';
import { NoIndexService } from '@shared/services/no-index.service';
import { TranslateDataPipe } from '@shared/pipes/translate-data.pipe';
// env
import { environment } from '@environments/environment';
import {
  provideClientHydration,
  withEventReplay,
  withIncrementalHydration,
} from '@angular/platform-browser';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaLoaderService,
  RecaptchaV3Module,
  ReCaptchaV3Service,
} from 'ng-recaptcha';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { NgxSpinnerModule } from 'ngx-spinner';

const config: SocketIoConfig = { url: environment.baseUrl.socket, options: {} };

export function HttpLoaderFactory(http: HttpClient) {
  const time = new Date().getTime();
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${time}`);
}

export const provideTranslation = () => ({
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

const inMemoryScrollingFeature = withInMemoryScrolling({
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
});

export const appConfig: ApplicationConfig = {
  providers: [
    InitService,
    UserService,
    AuthService,
    ProductsService,
    SuppliersService,
    AuctionsService,
    TendersService,
    ProjectsService,
    QuickOrderService,
    ElasticSearchService,
    CategoriesService,
    CommentsService,
    FavoritesService,
    SolutionsService,
    HelpService,
    LayoutService,
    StorageService,
    BrowserStorageService,
    StateManagementService,
    ChatService,
    MessagingService,
    PushNotificationsService,
    FirebaseService,
    HandleErrorService,
    PwjToastService,
    UploaderService,
    CurrencyService,
    DialogService,
    CanonicalService,
    NoIndexService,
    HasAccessPipe,
    TranslateDataPipe,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    SocketFactory,
    RecaptchaService,
    RecaptchaV3Module,
    ReCaptchaV3Service,
    RecaptchaLoaderService,
    SeoTagsService,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.siteKey },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideClientHydration(withEventReplay(), withIncrementalHydration()),
    provideToastr(),
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptors([BaseInterceptor, AuthorizationInterceptor])
    ),
    { provide: 'googleTagManagerId', useValue: environment.gtmId },
    provideRouter(routes, inMemoryScrollingFeature),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // NgxGoogleAnalyticsRouterModule,
    importProvidersFrom([
      TranslateModule.forRoot(provideTranslation()),
      // NgxGoogleAnalyticsModule.forRoot(environment.googleAnalytics),
      SocketIoModule.forRoot(config),
    ]),
    provideMatomo(
      {
        disabled: !environment.matomoEnabled,
        enableLinkTracking: 'enable-pseudo',
        siteId: environment.matomoSiteId,
        trackerUrl: environment.matomoTrackerUrl,
        scriptUrl: environment.matomoScriptUrl,
      },
      withRouter()
    ),
  ],
};
